import {
  Icon,
  SelectChangeEvent,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import {
  FunctionComponent,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useRouter } from "next/router";
import {
  useAddQueryParams,
  useQueryParams,
} from "../../../dataAccess/QueryParams";
import DropdownList, { DropdownItem } from "../DropdownList";
import SavingsAsc from "../../../assets/svg/savings-desc.svg";
import AlphaAZ from "../../../assets/svg/alpha-a-z.svg";
import AlphaZA from "../../../assets/svg/alpha-z-a.svg";
import Popularity from "../../../assets/svg/popularity-icon.svg";
import OnlyDesktop from "../OnlyDesktop";
import OnlyMobile from "../OnlyMobile";
import type { Settings } from "../../public/Search";

interface Option extends Pick<DropdownItem, "value"> {
  label?: string;
}

interface Props {
  options: Option[];
  settings: Settings["sort"];
}

const SORT_QUERY_PARAM_KEY = "sorting";

const StyledDownArrowIcon = styled(Icon)(({ theme }) => ({
  color: theme.palette.text.default,
}));

const StyledSortIcon = styled(Icon)(({ theme }) => ({
  color: theme.palette.text.default,
}));

const StyledSortIconImage = styled("img")(() => ({
  maxWidth: 24,
  maxHeight: 24,
}));

const StyledThemeColorIcon = styled(Icon)(({ theme }) => ({
  color: theme.palette.text.paper,
}));

const Label = styled(Typography)(() => ({
  maxWidth: 100,
}));

const SortButtonDropdown: FunctionComponent<Props> = ({
  options,
  settings,
  ...props
}: Props) => {
  const router = useRouter();
  const theme = useTheme();
  const setQueryParams = useAddQueryParams();
  const { sorting } = useQueryParams();

  const sortOptions: Record<
    DropdownItem["value"],
    { label: string; Icon: ReactNode }
  > = {
    "": {
      label: "",
      Icon: <></>,
    },
    recommended: {
      label: settings?.recommendedLabel || "Recommended",
      Icon:
        theme.settings.iconSet === "outlined" ? (
          <StyledThemeColorIcon>star_border</StyledThemeColorIcon>
        ) : (
          <StyledThemeColorIcon>star</StyledThemeColorIcon>
        ),
    },
    popular: {
      label: settings?.popularityLabel || "Popularity",
      Icon: <Popularity fill={theme.palette.text.paper} />,
    },
    savings: {
      label: settings?.greatestSavingsLabel || "Greatest Savings",
      Icon: <SavingsAsc fill={theme.palette.text.paper} />,
    },
    asc: {
      label: settings?.aZLabel || "Alphabetical (A to Z)",
      Icon: <AlphaAZ fill={theme.palette.text.paper} />,
    },
    desc: {
      label: settings?.ZaLabel || "Alphabetical (Z to A)",
      Icon: <AlphaZA fill={theme.palette.text.paper} />,
    },
  };

  const selectedSortOptionByQueryParam = useMemo(
    () => options.find((option) => option.value === sorting),
    [router],
  );

  const [selectedSortOption, setSelectedSortOption] = useState<Option>(
    selectedSortOptionByQueryParam || {
      value: "",
      label: "",
    },
  );

  const sortIcon = useMemo(() => {
    if (settings?.mobileSortIcon) {
      if (settings.mobileSortIcon.startsWith("http")) {
        return (
          <StyledSortIconImage
            src={settings.mobileSortIcon}
            alt={settings.label}
          />
        );
      }
      return <StyledSortIcon>{settings.mobileSortIcon}</StyledSortIcon>;
    }
    return <StyledSortIcon>sort</StyledSortIcon>;
  }, [settings]);

  const handleChange = useCallback(
    (event: SelectChangeEvent<DropdownItem>) => {
      const currentSortOption = options.find(
        (option) => option.value === event.target.value,
      );

      if (currentSortOption !== undefined) {
        setSelectedSortOption(currentSortOption);
        setQueryParams([
          {
            paramKey: "page_number",
            paramValue: null,
          },
          {
            paramKey: SORT_QUERY_PARAM_KEY,
            paramValue: currentSortOption.value,
          },
        ]);
      }
    },
    [options, setQueryParams],
  );

  return (
    <DropdownList
      {...props}
      showIconAsLabel
      icon={
        <>
          <OnlyDesktop>
            <StyledDownArrowIcon>keyboard_arrow_down</StyledDownArrowIcon>
          </OnlyDesktop>
          <OnlyMobile>{sortIcon}</OnlyMobile>
        </>
      }
      label={
        <>
          <OnlyDesktop>
            <Label noWrap>{settings?.label || "Sort"}</Label>
          </OnlyDesktop>
          <OnlyMobile>
            <></>
          </OnlyMobile>
        </>
      }
      options={options.map((option) => {
        const { Icon, label } = sortOptions[option.value];
        return {
          value: option.value,
          label,
          icon: !settings?.hideIcons && Icon,
        };
      })}
      onChange={handleChange}
      selectedValue={{
        value: selectedSortOption.value,
        label: selectedSortOption.label,
        icon: sortOptions[selectedSortOption.value].Icon,
      }}
    />
  );
};

export default SortButtonDropdown;
